/**
 * Overlay a jurisdiction package (skin + intake data) onto the golden
 * application-advisor Studio App definition.
 *
 * One App/Flow template × N city packages — do not fork demos per city.
 *
 * @param {object} appDef — golden IF-*-application-advisor app JSON
 * @param {object} pkg — jurisdiction package { packageId, name, catalog, intake, … }
 * @param {object} [opts]
 * @param {string} [opts.packageId]
 * @param {string} [opts.portalId] — business | residential | events
 * @param {object} [opts.defaults] — form prefill overrides
 * @param {boolean} [opts.citizenMode=true]
 * @param {boolean} [opts.publicAdvise] — catalog advise API (public demos). Studio omits this so form submit runs the live CLAW flow.
 * @param {object} [opts.theme] — force theme
 * @returns {object} cloned app definition
 */
export function applyJurisdictionToAdvisorApp(appDef, pkg, opts = {}) {
  if (!appDef || typeof appDef !== 'object') {
    throw new Error('appDef required');
  }
  const app = structuredClone(appDef);
  const packageId = String(
    opts.packageId || pkg?.packageId || pkg?.id || pkg?.catalog?.id || 'san-francisco',
  ).trim() || 'san-francisco';
  const intake = pkg?.intake || {};
  const catalog = pkg?.catalog || {};
  const cityName = pkg?.name || catalog.name || packageId;
  const portalId = opts.portalId || null;
  const portals = intake.portals && typeof intake.portals === 'object' ? intake.portals : {};
  const portal = portalId ? portals[portalId] : null;
  const defaults = {
    ...(portal?.defaults || {}),
    ...(opts.defaults || {}),
  };
  const citizenMode = opts.citizenMode !== false;
  const publicAdvise = opts.publicAdvise === true;

  const fields = Array.isArray(intake.formFields) ? intake.formFields : null;
  const mapCfg = intake.map && typeof intake.map === 'object' ? intake.map : null;
  const showWhenRules = intake.showWhenRules || null;
  const inputMapping = Array.isArray(intake.inputMapping) ? intake.inputMapping : null;
  const formTitle = portal?.formTitle || intake.defaultFormTitle || null;
  const formDescription = portal?.formDescription || intake.defaultFormDescription || null;
  const clearanceColors = intake.clearanceColors || null;
  const theme = opts.theme || portal?.theme || app.theme || { themeId: 'ocean', primaryColor: '#1565c0' };

  app.theme = { ...(app.theme || {}), ...theme };
  if (portal?.title) {
    app.name = portal.title;
  } else if (citizenMode) {
    app.name = `${String(cityName).replace(/^City and County of\s+/i, '')} Application Advisor`;
  }
  app.description = `${cityName} application advisor — permit / planning / license guidance`
    + (citizenMode ? ' (citizen outcomes; CLAW under the hood).' : ' (Studio App).');

  app.panels = (Array.isArray(app.panels) ? app.panels : []).map((panel) => {
    if (panel?.componentType === 'form' && panel.config) {
      const cfg = { ...panel.config };
      if (formTitle) cfg.formTitle = formTitle;
      if (formDescription) cfg.formDescription = formDescription;
      cfg.validateAddress = true;
      cfg.citizenSubmitLabel = 'Get my application path';
      const portalTypeOptions = Array.isArray(portal?.propertyTypeOptions) && portal.propertyTypeOptions.length
        ? portal.propertyTypeOptions.map((o) => String(o))
        : (Array.isArray(portal?.projectTypeOptions) && portal.projectTypeOptions.length
          ? portal.projectTypeOptions.map((o) => String(o))
          : null);
      const applyField = (f) => {
        let next = { ...f };
        if (f?.id === 'jurisdictionId' || f?.name === 'jurisdictionId') {
          next = { ...next, defaultValue: packageId };
        }
        // Portal shells (residential / business / events) narrow the property-type select.
        if (portalTypeOptions
          && (f?.id === 'propertyType' || f?.name === 'propertyType'
            || f?.id === 'projectType' || f?.name === 'projectType')
          && Array.isArray(f?.options)) {
          next = { ...next, options: [...portalTypeOptions] };
        }
        return next;
      };
      if (fields?.length) {
        cfg.formFields = fields.map(applyField);
      } else if (Array.isArray(cfg.formFields)) {
        cfg.formFields = cfg.formFields.map(applyField);
      }
      if (showWhenRules) cfg.showWhenRules = showWhenRules;
      if (inputMapping) cfg.inputMapping = inputMapping.map((m) => ({ ...m }));
      if (portal?.showWhenRules) cfg.showWhenRules = portal.showWhenRules;
      return { ...panel, config: cfg };
    }
    if (panel?.componentType === 'map' && panel.config) {
      return {
        ...panel,
        config: {
          ...panel.config,
          ...(mapCfg?.defaultZoom != null ? { defaultZoom: mapCfg.defaultZoom } : {}),
          ...(mapCfg?.defaultCenter ? { defaultCenter: mapCfg.defaultCenter } : {}),
          ...(mapCfg?.interactivePick != null ? { interactivePick: mapCfg.interactivePick } : {}),
          ...(mapCfg?.showClearanceLegend != null ? { showClearanceLegend: mapCfg.showClearanceLegend } : {}),
          ...(clearanceColors ? { clearanceColors } : {}),
        },
      };
    }
    if (panel?.componentType === 'table' && panel.config) {
      return {
        ...panel,
        config: {
          ...panel.config,
          emptyMessage: panel.config.emptyMessage
            || 'Submit project details on the Project step to build this applications list.',
        },
      };
    }
    if (panel?.componentType === 'rich_text' && panel.config?.interviewPhase === 'next-steps') {
      return {
        ...panel,
        config: {
          ...panel.config,
          emptyMessage: panel.config.emptyMessage
            || 'Submit project details to generate a downloadable summary.',
        },
      };
    }
    return panel;
  });

  const iflow = { ...(app.metadata?.interviewFlow || {}) };
  iflow.jurisdictionId = packageId;
  iflow.parcelAdapter = intake.parcelAdapter || catalog.parcelAdapter || pkg?.parcelAdapter || 'none';
  iflow.playbook = iflow.playbook || 'permits';
  // Public citizen help (ungated); Studio builders still use /studio/help/permits
  iflow.helpPath = citizenMode ? '/help/permits' : (iflow.helpPath || '/studio/help/permits');
  iflow.citizenMode = citizenMode;
  // Public demos: catalog advise API. Signed-in Studio: omit → form runs live CLAW skill loop.
  if (publicAdvise) iflow.publicAdvise = true;
  else if (opts.publicAdvise === false) iflow.publicAdvise = false;
  if (intake.staffSessionKey) iflow.staffSessionKey = intake.staffSessionKey;
  iflow.portalVariant = portalId || iflow.portalVariant || 'default';
  iflow.officialApplyUrl = catalog.officialApplyUrl || pkg?.officialApplyUrl || iflow.officialApplyUrl || null;
  if (clearanceColors) iflow.clearanceColors = clearanceColors;
  if (showWhenRules) iflow.showWhenRules = showWhenRules;
  if (intake.clawSkill) {
    iflow.claw = { ...(iflow.claw || {}), skill: intake.clawSkill };
  }
  if (Array.isArray(intake.clawTools) && intake.clawTools.length) {
    iflow.claw = { ...(iflow.claw || {}), tools: [...intake.clawTools] };
  }
  iflow.persistStaffSession = iflow.persistStaffSession !== false;
  if (intake.staffSessionKey) iflow.staffSessionKey = intake.staffSessionKey;
  iflow.advancePhaseOnAdvise = iflow.advancePhaseOnAdvise || 'checklist';
  iflow.initialPhaseId = iflow.initialPhaseId || 'advise';
  // Public intake gates (address + property type); Studio can override via intake.intakeGates
  iflow.intakeGates = {
    requireAddress: true,
    requirePropertyType: true,
    minAddressLength: 8,
    ...(intake.intakeGates && typeof intake.intakeGates === 'object' ? intake.intakeGates : {}),
    ...(opts.intakeGates && typeof opts.intakeGates === 'object' ? opts.intakeGates : {}),
  };
  if (Object.keys(defaults).length) {
    iflow.initialFormPrefill = defaults;
  }

  app.metadata = { ...(app.metadata || {}), interviewFlow: iflow };
  return app;
}

export default applyJurisdictionToAdvisorApp;
